// src/components/MarketFitDashboardModal.js

import React, { useEffect, useCallback } from 'react';
// --- THE FIX: Reuse the existing, complete CSS file ---
import styles from './HealthDashboardModal.module.css'; 
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faBullseye, faUsersCog, faHeart } from '@fortawesome/free-solid-svg-icons';

// NOTE: We no longer need a separate CSS file or a `getMarketFitTierColor` helper here.
// The class name will be passed in from the DiscoveryPage component, which now controls the color mapping.
// Helper to map tier names to CSS classes for consistent coloring
const getTierClassName = (tier) => {
    switch (tier?.toLowerCase()) {
        case 'prime':
        case 'excellent':
        case 'excellent fit':
            return styles.prime;
        case 'promising':
        case 'good':
        case 'high fit':
        case 'high':
            return styles.promising;
        case 'moderate fit':
        case 'moderate':
        case 'acceptable':
            return styles.acceptable;
        case 'low fit': 
        case 'low':   
        case 'mid-risk':
            return styles.midRisk;
        case 'high-risk':
        case 'poor':
        case 'poor fit':
            return styles.highRisk;
        default:
            return '';
    }
};

const MarketFitDashboardModal = ({ isOpen, onClose, data, isLoading, TierClassName }) => {
    const handleKeyDown = useCallback((event) => {
        if (event.key === 'Escape') {
            onClose();
        }
    }, [onClose]);

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, handleKeyDown]);

    if (!isOpen) return null;

    // Destructure market fit specific data
    const score = data?.marketFitScore ?? 0;
    const tier = data?.marketFitTier ?? 'N/A';
    const rationale = data?.rationale ?? 'Rationale not available.';
    const breakdown = data?.scoringBreakdown;

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h3>Market Fit Analysis</h3>
                    <button onClick={onClose} className={styles.closeButton} aria-label="Close">
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {isLoading ? (
                    <div className={styles.loadingContainer}>
                        <div className={styles.spinner}></div>
                        <p>Loading Market Fit Analysis...</p>
                    </div>
                ) : !data ? (
                     <div className={styles.errorContainer}>
                        <h4>Analysis Not Available</h4>
                        <p>Market Fit data could not be generated for this channel.</p>
                    </div>
                ) : (
                    <>
                        <div className={styles.mainGrid}>
                            <div className={styles.scoreCard}>
                                {/* --- THE FIX: Use the tierClassName prop for consistent coloring --- */}
                                <div className={`${styles.tierBadge} ${getTierClassName(tier)}`}>
                                    {tier}
                                </div>
                                <div className={styles.scoreValue}>{score.toFixed(2)}</div>
                                <div className={styles.scoreLabel}>Total Market Fit Score</div>
                            </div>
                            <div className={styles.rationaleCard}>
                                <h4>Analyst Rationale</h4>
                                <p>{rationale}</p>
                            </div>
                        </div>

                        <h4>Scoring Breakdown</h4>
                        <div className={styles.kpiGrid}>
                           <div className={styles.kpiCard}>
                                <FontAwesomeIcon icon={faBullseye} className={styles.kpiIcon} />
                                <div className={styles.kpiValue}>{breakdown?.problemSolutionResonance?.score?.toFixed(1) ?? 'N/A'}/5</div>
                                <div className={styles.kpiLabel}>Problem-Solution Resonance</div>
                                <div className={styles.kpiDetail}>{breakdown?.problemSolutionResonance?.value ?? 'N/A'}</div>
                            </div>
                             <div className={styles.kpiCard}>
                                <FontAwesomeIcon icon={faUsersCog} className={styles.kpiIcon} />
                                <div className={styles.kpiValue}>{breakdown?.audiencePersonaAlignment?.score?.toFixed(1) ?? 'N/A'}/5</div>
                                <div className={styles.kpiLabel}>Audience & Persona Alignment</div>
                                <div className={styles.kpiDetail}>{breakdown?.audiencePersonaAlignment?.value ?? 'N/A'}</div>
                            </div>
                           <div className={styles.kpiCard}>
                                <FontAwesomeIcon icon={faHeart} className={styles.kpiIcon} />
                                <div className={styles.kpiValue}>{breakdown?.brandValueAlignment?.score?.toFixed(1) ?? 'N/A'}/5</div>
                                <div className={styles.kpiLabel}>Brand Value Alignment</div>
                                <div className={styles.kpiDetail}>{breakdown?.brandValueAlignment?.value ?? 'N/A'}</div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default MarketFitDashboardModal;